import type { HTMLAttributes } from 'react';
import { css } from '@emotion/react';
import { useCopy } from '@frontend/document';
import { Icon as DSIcon } from '@frontend/icons';
import { theme } from '@frontend/theme';
import { Heading, Text, useTooltip } from '@frontend/design-system';

type Props = HTMLAttributes<HTMLDivElement> & {
  children: React.ReactNode;
};
export const OutlinedCard = ({ children, ...rest }: Props) => {
  return (
    <div css={card} {...rest}>
      {children}
    </div>
  );
};

type SimpleProps = {
  header: string;
  body: string;
  Icon?: React.ReactNode;
  canCopyToClipboard?: boolean;
};
export const SimpleInfoOutlinedCard = ({ header, body, Icon, canCopyToClipboard }: SimpleProps) => {
  const [wasCopied, copy] = useCopy(body);
  const { tooltipProps, triggerProps, Tooltip } = useTooltip();
  return (
    <OutlinedCard>
      <Heading level={3}>
        {header}{' '}
        {canCopyToClipboard && (
          <>
            <div
              {...triggerProps}
              css={css`
                vertical-align: middle;
                display: inline-block;
                cursor: pointer;
              `}
            >
              <DSIcon
                onClick={() => copy()}
                name={wasCopied ? 'check' : 'copy'}
                color={wasCopied ? 'success' : 'light'}
              />
              <Tooltip {...tooltipProps}>{wasCopied ? 'Copied!' : 'Copy to Clipboard'}</Tooltip>
            </div>
          </>
        )}
      </Heading>
      <Text
        color='subdued'
        css={css`
          display: flex;
          gap: ${theme.spacing(1)};
          align-items: center;
        `}
      >
        {Icon}
        {body}
      </Text>
    </OutlinedCard>
  );
};

const card = css`
  padding: ${theme.spacing(2)};
  border-radius: ${theme.borderRadius.medium};
  border: 1px solid ${theme.colors.neutral20};
`;
